import * as React from 'react';
import { ImageServiceApi } from '../../api/admin/apis';
import { WorkApi, ProjectApi, OrganisationApi, PersonApi } from 'api/core/apis';
import { connectOAuth2, OAuth2AwareProps } from '../../Auth/utils/connect';
import { DashboardType } from '../../Dashboard/types';
import { DetailView } from '../data';
import { DetailViewProps, DetailViewType, ViewDashboardKeys, ViewDashboards, ViewType } from '../types';
import { useEffect, useState } from 'react';
import { useStatusCode } from 'Utils/StatusCodeHandler';
import { OrganisationDTO, PersonDTO, ProjectDTO, WorkDTO } from '../../api/core';

interface FetchRequestType {
  id: number;
}

export interface Props {
  entity: ViewType;
  id: number;
}

type OwnProps = OAuth2AwareProps<Props>;

function ConnectedInnerDetailView (props: OwnProps) {
  const [viewLoading, setViewLoading] = useState<boolean>(false);
  const [viewError, setViewError] = useState<string|null>(null);
  const [viewEntity, setViewEntity] = useState<ViewType>(props.entity);
  const [viewId, setViewId] = useState<number>(props.id);
  const [view, setView] = useState<DetailViewType|null>(null);
  const [dashboardLoading, setDashboardLoading] = useState<number>(0);
  const [dashboard, setDashboard] = useState<ViewDashboards>(null);
  const [dashboardError, setDashboardError] = useState<string|null>(null);
  const [lastRequest, setLastRequest] = useState<number>(0);
  const [avatarURL, setAvatarURL] = useState<string>(null);
  const workApi: WorkApi = props.apiWithAuth(new WorkApi());
  const projectApi: ProjectApi = props.apiWithAuth(new ProjectApi());
  const organisationApi: OrganisationApi = props.apiWithAuth(new OrganisationApi());
  const personApi: PersonApi = props.apiWithAuth(new PersonApi());
  const imageApi: ImageServiceApi = props.apiWithAuth(new ImageServiceApi());
  const { setStatusCode } = useStatusCode();
  const dashboardData: any[] = [];

  useEffect((): void=>{
    void fetchUpdate(props.id);
  },[]);

  useEffect((): void => {
    if(viewEntity !== props.entity || viewId !== props.id) {
      void fetchUpdate(props.id);
    }
  });

  const getRenderProps = (): DetailViewProps =>{
    return {
      entity: viewEntity,
      id: viewId,
      viewLoading,
      viewError,
      view,
      dashboardLoading,
      // dashboardError: this.state.dashboardError,
      dashboardData: dashboard,
      avatarURL: viewEntity === DashboardType.PEOPLE ? avatarURL : undefined,
    };
  };

  const  fetchUpdate = async (id: number): Promise<void> => {
    await Promise.all([fetch({ id }), fetchExtras(props.entity, id)]);
  };

  // TODO fallthrought vorgesehen?
  const fetchExtras =  async (entity: ViewType, id: number): Promise<void> => {
    switch (entity) {
      case ViewType.PEOPLE:
         await Promise.all([
          fetchDashboard('projects', () => {
            return personApi.getProjectsOfPerson({ personId: id });
          }),
          fetchDashboard('organisations', () => {
            return personApi.getOrganisationsOfPerson({ personId: id });
          }),
        ]);
      case ViewType.PROJECTS:
         await Promise.all([
          fetchDashboard('persons', () => {
            return projectApi.getPersonsInProject({ projectId: id });
          }),
          fetchDashboard('personsByProjectIncludingChilds', () => {
            return projectApi.getPersonsIncludingChildProjects({ projectId: id });
          }),
        ]);
      default:
        return;
    }
  };

  const fetchDashboard = async (key: ViewDashboardKeys, ps: () => Promise<number[]>) => {
    let arr: number[] = [];
    try {
      arr = await ps();
    } catch (error) {
      if (error instanceof TypeError) {
        setDashboardError(error.message);
        setDashboard(null);
      } else if (typeof error === 'string') {
        setDashboardError(error);
        setDashboard(null);
      } else {
        setDashboardError('Unknown Error');
        setDashboard(null);
      }
    }
    dashboardData.push({...dashboard, [key]:arr});
    const dashboardDataObj = Object.assign({}, ...dashboardData );
    setDashboard(dashboardDataObj);
  };

   const fetchAvatar =  async (personId: number): Promise<void> => {
    try {
      const avatar = await imageApi.findByPersonId({ personId });
      setAvatarURL(avatar != null ? URL.createObjectURL(avatar) : null);
    } catch (e) {
      if ('status' in e) {
        const response = e as Response;

        if (response.status === 404) {
          // Having no avatar is not an error
          return;
        }

        throw new Error(`Status Code ${response.status} ${response.statusText}:\n${response.text()}`);
      }
      throw e;
    }
  };

  const fetch = async (request: FetchRequestType): Promise<void> => {
    // const _logger = logger.find('fetch');
    const time: number = Date.now();
    fetchLoading(time);

    try {
      // Load and await response
      const response: DetailViewType = await ((req): Promise<WorkDTO> | Promise<PersonDTO> | Promise<OrganisationDTO> | Promise<ProjectDTO> => {
        switch (props.entity) {
          case ViewType.WORKS:
            return workApi.getWork({workId: req.id});
          case ViewType.PEOPLE:
            return personApi.getPerson({personId: req.id});
          case ViewType.ORGS:
            return organisationApi.getReducedOrganisation1({
              organisationId: {...req}.id,
              accept: 'application/orga.reduced+json',
            });
          case ViewType.PROJECTS:
            return projectApi.getProjectReduced1({
              projectId: {...req}.id,
              accept: 'application/project.reduced+json',
            });
          default:
            throw new Error(`Invalid ViewType "${props.entity}"`);
        }
      })(request);

      fetchSuccess(time, response);

      if (props.entity === DashboardType.PEOPLE) {
        await fetchAvatar(response.id).catch((e): void => {
          // _logger.error(`Fetching avatar for personId ${response.id} failed:`, e);
        });
      }
    } catch (error) {
      setStatusCode({status: error.status});
      if (error instanceof TypeError) {
        fetchFailed(time, error.message);
      } else if (typeof error === 'string') {
        fetchFailed(time, error);
      } else {
        fetchFailed(time, 'Unknown error');
      }
    }
  };

  // Update state when the fetch request is starting
  const fetchLoading = (time: number): void => {
    const { id: viewId, entity: viewEntity } = props;
    setViewLoading(true);
    setViewError(null);
    setViewId(viewId);
    setViewEntity(viewEntity);
    setView(null);
    setLastRequest(time);
  };

  // Update sate when the fetch request failed
  const fetchFailed = (time: number, error: string): void => {
    const { id: viewId, entity: viewEntity } = props;
    // Ensure to only update for the most recent request
    if (time < lastRequest) {
      return;
    }
    setViewLoading(false);
    setViewError(error);
    setViewId(viewId);
    setViewEntity(viewEntity);
    setView(null);
    setLastRequest(time);
  };

  // Update state when the fetch request succeeded
  const fetchSuccess = (time: number, response: DetailViewType) => {
    const { id: viewId, entity: viewEntity } = props;
    // Ensure to only update for the most recent request
    if (time < lastRequest) {
      return;
    }
    setViewLoading(false);
    setViewError(null);
    setViewId(viewId);
    setViewEntity(viewEntity);
    setView(response);
    setLastRequest(time);
  };

  const _props: DetailViewProps = getRenderProps();

  return React.createElement(DetailView(viewEntity), _props);
}

export const ConnectedDetailView: React.FunctionComponent<Props> = connectOAuth2(ConnectedInnerDetailView);

export default ConnectedDetailView;
